import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { PartnersService } from '../../services/partners.service';
import { Partner } from '../../interfaces/partners.interface';
import { environment } from '../../../environments/environment';
import { OffersService } from '../../services/offers.service';
import { Offer } from '../../interfaces/offers.interface';
import { Title } from '@angular/platform-browser';
import { APP_NAME } from '../../app.config';
import { AfterViewInit } from '@angular/core';
import * as L from 'leaflet';
import { Subject, Subscription, interval, forkJoin, of } from 'rxjs';
import { takeUntil, catchError, debounceTime } from 'rxjs/operators';
import { PersonsService } from '../../services/persons.service';
import { Person, PersonCamapignAggregation } from '../../interfaces/persons.interface';
import { PartnerProductsService } from '../../services/partner-products.service';
import { PartnerProduct } from '../../interfaces/partner-product.interface';
import { CamapignService } from '../../services/campaign.service';
import { Camapign } from '../../interfaces/campaigns.interface';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmComponent } from '../../shared/dialog-confirm/dialog-confirm.component';

@Component({
  selector: 'app-partnerspartner-details',
  standalone: false,
  templateUrl: './partnerspartner-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./partnerspartner-details.component.scss']
})
export class PartnerspartnerDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  Number(arg0: string | undefined): number | (() => number) {
    throw new Error('Method not implemented.');
  }
  partner?: Partner;
  branches: Partner[] = [];
  imagesUrl = `${environment.ImagesUrl}/partners/`;

  private leafletMap: L.Map | null = null;
  private partnerMarker: L.Marker | null = null;
  private branchMarkers: L.Marker[] = [];
  private readonly polandBounds = {
    minLat: 48.8,
    maxLat: 55.1,
    minLng: 14.0,
    maxLng: 24.3
  };
  mapView = true;
  satelliteView = true;
  currentLocation?: { lat: number; lng: number };
  autoLocationUpdateEnabled = false;
  isGeolocationSupported = false;

  private locationUpdateSubscription?: Subscription;
  private readonly LOCATION_UPDATE_INTERVAL = 2 * 60 * 1000;
  private combineOffersSubject = new Subject<void>();
  private dataLoaded = false;

  offers: Offer[] = [];
  branchOffers: { [key: number]: Offer[] } = {};
  persons: Person[] = [];
  personsLoading = false;
  partnerProducts: PartnerProduct[] = [];
  productsLoading = false;
  currentCampaigns: Camapign[] = [];
  campaignsLoading = false;
  private $destroy = new Subject<void>();

  constructor(private route: ActivatedRoute, private location: Location, private partnersService: PartnersService, private offersService: OffersService, private router: Router, private titleService: Title, private personsService: PersonsService, private partnerProductsService: PartnerProductsService, private camapignService: CamapignService, private dialog: MatDialog) {
    this.isGeolocationSupported = !!navigator?.geolocation;
    
    // Debounce combine offers to prevent excessive operations
    this.combineOffersSubject.pipe(
      debounceTime(300),
      takeUntil(this.$destroy)
    ).subscribe(() => {
      this.combineAllOffersInternal();
    });
  }
  ngOnInit(): void {
    // Set initial loading states
    this.productsLoading = true;
    this.personsLoading = true;
    this.campaignsLoading = true;
    
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.loadPartnerData(+id);
      }
    });
  }

  private loadPartnerData(partnerId: number): void {
    // Load partner first to get basic info
    this.partnersService.getPartner(partnerId).pipe(
      takeUntil(this.$destroy)
    ).subscribe({
      next: (partner) => {
        this.partner = {} as Partner; // Clear partner to show loading state
        this.partner.name = partner.name;
        this.partner.id = partner.id;
        this.partner.latitude = partner.latitude;
        this.partner.longitude = partner.longitude;
        this.partner.url = partner.url;
        this.partner.file = partner.file;
        this.partner.info = partner.info;
        this.partner.contact = this.cleanContactText(partner.contact).replace(/\n/g, '<br/>');

        //this.partner = partner;
        this.titleService.setTitle(APP_NAME + ' - ' + partner.name);
        
        // Now load all related data in parallel using forkJoin
        this.loadAllRelatedData(partnerId);
      },
      error: () => {
        this.partner = undefined;
        this.productsLoading = false;
        this.personsLoading = false;
        this.campaignsLoading = false;
      }
    });
  }

  private loadAllRelatedData(partnerId: number): void {
    // Coordinate all API calls in parallel
    forkJoin({
      branches: this.partnersService.getData({ parent_id: partnerId }).pipe(
        catchError(() => of({ items: [] }))
      ),
      offers: this.offersService.getOffers({ partner_id: partnerId }).pipe(
        catchError(() => of({ items: [] }))
      ),
      products: this.partnerProductsService.getCurrentList({
        partner_id: partnerId,
        order_by: 'sort',
        order: 'desc',
      }).pipe(
        catchError(() => of({ items: [] }))
      ),
      persons: this.personsService.getCurrent().pipe(
        catchError(() => of([]))
      ),
      campaigns: this.camapignService.getCurrentList({ partner_id: partnerId }).pipe(
        catchError(() => of({ items: [] }))
      )
    }).pipe(
      takeUntil(this.$destroy)
    ).subscribe({
      next: (results) => {
        // Set branches
        this.branches = results.branches.items || [];
        
        // Set main offers
        this.offers = results.offers.items || [];
        
        // Set products
        this.partnerProducts = results.products.items || [];
        this.productsLoading = false;
        
        // Set persons
        this.persons = results.persons || [];
        this.personsLoading = false;
        
        // Set campaigns
        this.currentCampaigns = results.campaigns.items || [];
        this.campaignsLoading = false;
        
        // Load branch offers if there are branches
        if (this.branches.length > 0) {
          this.loadAllBranchOffers();
        } else {
          // No branches, finalize data loading
          this.finalizeDataLoading();
        }
      },
      error: (err) => {
        console.error('Error loading partner data:', err);
        this.productsLoading = false;
        this.personsLoading = false;
        this.campaignsLoading = false;
        this.finalizeDataLoading();
      }
    });
  }

  private loadAllBranchOffers(): void {
    const branchOfferRequests = this.branches.map(branch =>
      this.offersService.getOffers({ partner_id: branch.id }).pipe(
        catchError(() => of({ items: [] }))
      )
    );

    if (branchOfferRequests.length === 0) {
      this.finalizeDataLoading();
      return;
    }

    forkJoin(branchOfferRequests).pipe(
      takeUntil(this.$destroy)
    ).subscribe({
      next: (results) => {
        results.forEach((result, index) => {
          const branchId = this.branches[index].id;
          this.branchOffers[branchId] = result.items || [];
        });
        
        // Combine all offers once
        this.combineAllOffersInternal();
        this.finalizeDataLoading();
      },
      error: () => {
        this.finalizeDataLoading();
      }
    });
  }

  private finalizeDataLoading(): void {
    this.dataLoaded = true;
    // Update map with all markers after a short delay
    setTimeout(() => {
      if (this.leafletMap) {
        this.addAllMarkers();
      }
    }, 100);
  }

  ngAfterViewInit(): void {
    // Initialize map after view is ready
    setTimeout(() => {
      this.initializeMap();
    }, 100);
  }

  private initializeMap(): void {
    const mapDiv = document.getElementById('partner-map');
    if (!mapDiv || this.leafletMap) {
      return; // Already initialized or div doesn't exist
    }

    const lat = this.getCenterLat();
    const lng = this.getCenterLng();
    this.leafletMap = L.map('partner-map').setView([lat, lng], 15);

    this.satelliteView = false;
    this.switchToNormalView();

    // Markers will be added by finalizeDataLoading after data is ready
  }

  getPartnerMapCenter(): { lat: number; lng: number } {
    return {
      lat: this.getCenterLat(),
      lng: this.getCenterLng()
    };
  }

  getPartnerPointerLocal(): { lat: number; lng: number; title?: string; subtitle?: string } | null {
    if (!this.partner) {
      return null;
    }

    const coordinates = this.getValidCoordinates(this.partner);
    if (!coordinates) {
      return null;
    }

    return {
      lat: coordinates.lat,
      lng: coordinates.lng,
      title: this.partner.name,
      subtitle: 'Główna lokalizacja'
    };
  }

  // These methods are now handled by loadAllRelatedData using forkJoin
  // Kept for backward compatibility if called elsewhere

  openCampaignDescription(campaign: Camapign): void {
    this.dialog.open(DialogConfirmComponent, {
      data: {
        title: campaign.name,
        html: campaign.description,
        hideCancel: true,
        okLabel: 'Zamknij'
      },
      maxWidth: '600px',
      width: '90vw'
    });
  }

  getPartnerAggregation(person: Person): PersonCamapignAggregation | null {
    if (!this.partner || !person.aggregations?.length) return null;
    return person.aggregations.find(a => a.partner_id === this.partner!.id) ?? null;
  }

  getPersonsForPartner(): Person[] {
    return this.persons.filter(p => this.getPartnerAggregation(p) !== null);
  }

  // ── Contact parsing ──────────────────────────────────────────────────────────

  /** Split the free-text contact field into typed items for display. */
  getContactItems(): Array<{ type: 'phone' | 'email' | 'text'; value: string }> {
    if (!this.partner?.contact) return [];
    return this.partner.contact
      .split(/[\n;]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(part => {
        if (this.isEmail(part))  return { type: 'email' as const,  value: part };
        if (this.isPhone(part))  return { type: 'phone' as const,  value: part };
        return                          { type: 'text'  as const,  value: part };
      });
  }

  private isEmail(s: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  private isPhone(s: string): boolean {
    return /^[\+\d][\d\s\-\(\)\.\/]{5,}$/.test(s);
  }

  /** Normalise phone for tel: href (digits, +, hyphens only) */
  toTelHref(phone: string): string {
    return 'tel:' + phone.replace(/[^\d\+]/g, '');
  }

  /** Strip HTML tags from a contact text fragment, converting <br> to newlines. */
  cleanContactText(html: string): string {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&ndash;/gi, ' - ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&nbsp/gi, ' ')
      .replace(/&oacute;/gi, 'ó')
      .trim();
  }

  ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
    this.stopAutoLocationUpdate();
    this.clearRoute();
  }

  switchToNormalView() {
    this.satelliteView = false;
    if (this.leafletMap) {
      this.leafletMap.eachLayer(layer => {
        if (layer instanceof L.TileLayer) {
          this.leafletMap!.removeLayer(layer);
        }
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.leafletMap);
    }
  }

  switchToSatelliteView() {
    this.satelliteView = true;
    if (this.leafletMap) {
      this.leafletMap.eachLayer(layer => {
        if (layer instanceof L.TileLayer) {
          this.leafletMap!.removeLayer(layer);
        }
      });
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
      }).addTo(this.leafletMap);
    }
  }

  showCurrentLocation(enableAutoUpdate: boolean = false) {
    if (!this.leafletMap) return;

    if (!this.isGeolocationSupported) {
      alert('Geolokalizacja nie jest wspierana przez Twoją przeglądarkę.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        this.currentLocation = { lat, lng };

        this.leafletMap!.setView([lat, lng], 15);
        this.updateCurrentLocationMarker(lat, lng, 'Twoja lokalizacja');

        if (enableAutoUpdate && !this.autoLocationUpdateEnabled) {
          this.startAutoLocationUpdate();
        }
      },
      (error) => {
        let errorMessage = 'Nie można pobrać lokalizacji: ';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Dostęp do lokalizacji został zablokowany. Sprawdź ustawienia przeglądarki.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Informacje o lokalizacji są niedostępne.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Przekroczono czas oczekiwania na pobranie lokalizacji.';
            break;
          default:
            errorMessage += error.message;
            break;
        }

        alert(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  }

  navigateToPartner() {
    if (!this.partner || !this.leafletMap) {
      return;
    }

    const destination = this.getValidCoordinates(this.partner);
    if (!destination) {
      alert('Brak poprawnej lokalizacji partnera do nawigacji.');
      return;
    }

    if (this.currentLocation) {
      this.drawRoute(this.currentLocation, destination);
      return;
    }

    this.showCurrentLocation(false);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const from = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        this.currentLocation = from;
        this.drawRoute(from, destination);
      },
      () => {
        alert('Nie można pobrać Twojej lokalizacji do wyznaczenia trasy.');
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  }

  clearRoute() {
    if (!this.leafletMap) {
      return;
    }

    if ((this.leafletMap as any)._routeLine) {
      this.leafletMap.removeLayer((this.leafletMap as any)._routeLine);
      (this.leafletMap as any)._routeLine = null;
    }

    if ((this.leafletMap as any)._routeMarkers) {
      (this.leafletMap as any)._routeMarkers.forEach((marker: L.Marker) => {
        this.leafletMap!.removeLayer(marker);
      });
      (this.leafletMap as any)._routeMarkers = [];
    }
  }

  toggleAutoLocationUpdate() {
    if (!this.isGeolocationSupported) {
      alert('Geolokalizacja nie jest wspierana przez Twoją przeglądarkę.');
      return;
    }

    if (this.autoLocationUpdateEnabled) {
      this.stopAutoLocationUpdate();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        this.startAutoLocationUpdate();
      },
      (error) => {
        let errorMessage = 'Nie można włączyć automatycznych aktualizacji lokalizacji. ';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Dostęp do lokalizacji został zablokowany. Sprawdź ustawienia przeglądarki.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Informacje o lokalizacji są niedostępne.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Przekroczono czas oczekiwania na pobranie lokalizacji. Spróbuj ponownie.';
            break;
          default:
            errorMessage += 'Wystąpił nieznany błąd podczas pobierania lokalizacji.';
            break;
        }

        alert(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }

  getAutoLocationUpdateStatus(): string {
    return this.autoLocationUpdateEnabled
      ? 'Automatyczne aktualizacje włączone'
      : 'Automatyczne aktualizacje wyłączone';
  }

  private startAutoLocationUpdate() {
    this.stopAutoLocationUpdate();
    this.autoLocationUpdateEnabled = true;

    this.locationUpdateSubscription = interval(this.LOCATION_UPDATE_INTERVAL).subscribe(() => {
      this.updateCurrentLocationSilently();
    });
  }

  private stopAutoLocationUpdate() {
    if (this.locationUpdateSubscription) {
      this.locationUpdateSubscription.unsubscribe();
      this.locationUpdateSubscription = undefined;
    }
    this.autoLocationUpdateEnabled = false;
  }

  private updateCurrentLocationSilently() {
    if (!this.isGeolocationSupported || !this.leafletMap) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        this.currentLocation = { lat, lng };
        this.updateCurrentLocationMarker(lat, lng, 'Aktualna lokalizacja');
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          this.stopAutoLocationUpdate();
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }

  private updateCurrentLocationMarker(lat: number, lng: number, popupText: string) {
    if (!this.leafletMap) return;

    if ((this.leafletMap as any)._currentLocationMarker) {
      this.leafletMap.removeLayer((this.leafletMap as any)._currentLocationMarker);
    }

    const redIcon = L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    const marker = L.marker([lat, lng], { icon: redIcon })
      .addTo(this.leafletMap)
      .bindPopup(popupText);

    (this.leafletMap as any)._currentLocationMarker = marker;
  }

  private drawRoute(from: { lat: number, lng: number }, to: { lat: number, lng: number }) {
    if (!this.leafletMap) return;

    this.clearRoute();

    const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson&steps=true`;

    fetch(osrmUrl)
      .then(response => response.json())
      .then(data => {
        if (!data.routes || data.routes.length === 0) {
          alert('Nie znaleziono trasy do wskazanego punktu.');
          return;
        }

        const route = data.routes[0];
        const coordinates = route.geometry.coordinates;
        const latLngs = coordinates.map((coord: number[]) => [coord[1], coord[0]] as [number, number]);

        const routeLine = L.polyline(latLngs, {
          color: '#2196F3',
          weight: 5,
          opacity: 0.8,
          dashArray: '10, 5'
        }).addTo(this.leafletMap!);

        (this.leafletMap as any)._routeLine = routeLine;

        const startIcon = L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        const endIcon = L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        const startMarker = L.marker([from.lat, from.lng], { icon: startIcon })
          .addTo(this.leafletMap!)
          .bindPopup('Start - Twoja lokalizacja');

        const endMarker = L.marker([to.lat, to.lng], { icon: endIcon })
          .addTo(this.leafletMap!)
          .bindPopup('Cel - Lokalizacja partnera');

        (this.leafletMap as any)._routeMarkers = [startMarker, endMarker];
        this.leafletMap.fitBounds(routeLine.getBounds().pad(0.1));
      })
      .catch(() => {
        alert('Błąd podczas wyznaczania trasy. Spróbuj ponownie.');
      });
  }

  private addAllMarkers() {
    if (!this.leafletMap) {
      return; // Map not initialized yet
    }
    this.addPartnerMarker();
    this.addBranchMarkers();
  }

  private addPartnerMarker() {
    if (this.leafletMap && this.partner) {
      const coordinates = this.getValidCoordinates(this.partner);
      if (!coordinates) {
        return;
      }
      const { lat, lng } = coordinates;

      // Remove previous marker if exists
      if (this.partnerMarker) {
        this.leafletMap.removeLayer(this.partnerMarker);
      }

      const greenIcon = L.icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });

      this.partnerMarker = L.marker([lat, lng], { icon: greenIcon })
        .addTo(this.leafletMap)
        .bindPopup(`<strong>${this.partner.name}</strong><br/><em>Główna lokalizacja</em>`)
        .openPopup();

      this.leafletMap.setView([lat, lng], 15);
    }
  }

  private addBranchMarkers() {
    if (!this.leafletMap) return;

    // Remove existing branch markers
    this.branchMarkers.forEach(marker => {
      this.leafletMap!.removeLayer(marker);
    });
    this.branchMarkers = [];

    // Add markers for each branch
    this.branches.forEach(branch => {
      const coordinates = this.getValidCoordinates(branch);
      if (coordinates) {
        const { lat, lng } = coordinates;

        const blueIcon = L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        const branchMarker = L.marker([lat, lng], { icon: blueIcon })
          .addTo(this.leafletMap!)
          .bindPopup(`<strong>${branch.name}</strong><br/><em>Filia</em>`);

        this.branchMarkers.push(branchMarker);
      }
    });

    // Adjust map view to fit all markers
    this.fitMapToAllMarkers();
  }

  private fitMapToAllMarkers() {
    if (!this.leafletMap) return;

    const allMarkers: L.Marker[] = [];
    
    if (this.partnerMarker) {
      allMarkers.push(this.partnerMarker);
    }
    
    allMarkers.push(...this.branchMarkers);

    if (allMarkers.length > 1) {
      const group = new L.FeatureGroup(allMarkers);
      this.leafletMap.fitBounds(group.getBounds().pad(0.1));
    } else if (allMarkers.length === 1) {
      // If only one marker, center on it
      const latLng = allMarkers[0].getLatLng();
      this.leafletMap.setView(latLng, 15);
    }
  }

  // Trigger debounced combine (if needed externally)
  private combineAllOffers() {
    this.combineOffersSubject.next();
  }

  // Internal optimized combine method
  private combineAllOffersInternal() {
    // Start with main partner offers
    const allOffers: Offer[] = [...this.offers];
    
    // Add all branch offers
    Object.values(this.branchOffers).forEach(branchOfferList => {
      allOffers.push(...branchOfferList);
    });
    
    // Remove duplicates using Set for better performance
    const seenIds = new Set<number>();
    this.offers = allOffers.filter(offer => {
      if (seenIds.has(offer.id)) {
        return false;
      }
      seenIds.add(offer.id);
      return true;
    });
  }

  goToOfferDetails(id: number) {
    this.router.navigate(['/oferta', id]);
  }

  goBack() {
    this.location.back();
  }

  getCenterLat(): number {
    const coordinates = this.partner ? this.getValidCoordinates(this.partner) : null;
    if (coordinates) {
      return coordinates.lat;
    }
    return 51.9355; // default Zielona Góra latitude
  }
  
  goToPartnerDetails(id: number, name?: string) {
    let urlName = name
      ? name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      : 'partner';
    this.router.navigate(['/partner', urlName, id]);
  }

  getCenterLng(): number {
    const coordinates = this.partner ? this.getValidCoordinates(this.partner) : null;
    if (coordinates) {
      return coordinates.lng;
    }
    return 15.5064; // default Zielona Góra longitude
  }

  isValidPosition(partner: Partner): boolean {
    return !!this.getValidCoordinates(partner);
  }

  getMarkerPosition(partner: Partner): { lat: number, lng: number } {
    const coordinates = this.getValidCoordinates(partner);

    return {
      lat: coordinates?.lat ?? 51.9355,
      lng: coordinates?.lng ?? 15.5064

    };
  }

  private parseCoordinate(value?: string): number | null {
    if (!value) {
      return null;
    }

    const parsedValue = Number(value.toString().trim().replace(',', '.'));
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private isInsidePoland(lat: number, lng: number): boolean {
    return lat >= this.polandBounds.minLat
      && lat <= this.polandBounds.maxLat
      && lng >= this.polandBounds.minLng
      && lng <= this.polandBounds.maxLng;
  }

  private getValidCoordinates(partner: Partner): { lat: number; lng: number } | null {
    const lat = this.parseCoordinate(partner.latitude);
    const lng = this.parseCoordinate(partner.longitude);

    if (lat === null || lng === null) {
      return null;
    }

    if (!this.isInsidePoland(lat, lng)) {
      return null;
    }

    return { lat, lng };
  }
}
